import type { IPayout, IPayout2 } from "@gen/app-query";
import { PayoutStatus } from "@gen/app-query";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";

import { PublicSvgIcon } from "~/components/public_svg_icon";
import { StatusChip } from "~/components/status_chip";
import { useConfig } from "~/contexts/config";
import { MoneyFactory } from "~/utilities/money";

import {
  formatPayoutDate,
  isAdminPayout,
  payoutStatusColors,
  payoutStatusLabels,
} from "./payoutData";

function SummaryField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <Stack flexDirection="column" sx={{ minWidth: 0 }}>
      <Typography variant="body1DashedDivider">{label}</Typography>
      {children}
    </Stack>
  );
}

function SummaryValue({
  children,
  dataTestId,
}: {
  children: ReactNode;
  dataTestId: string;
}) {
  return (
    <Typography
      variant="body2"
      sx={{ color: "text.secondary", wordBreak: "break-all" }}
      data-testid={dataTestId}
    >
      {children}
    </Typography>
  );
}

function CopyButton({
  value,
  label,
  dataTestId,
}: {
  value: string;
  label: string;
  dataTestId?: string;
}) {
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch (err) {
      console.error("Couldn't copy value to clipboard:", err);
    }
  };

  return (
    <Tooltip title={label}>
      <IconButton
        size="small"
        aria-label={label}
        data-testid={dataTestId}
        onClick={(e) => {
          e.stopPropagation();
          handleCopy();
        }}
      >
        <PublicSvgIcon name="content_copy_duplicate" size={16} />
      </IconButton>
    </Tooltip>
  );
}

export interface PayoutSummaryProps {
  payout: IPayout | IPayout2;
}

export function PayoutSummary({ payout }: PayoutSummaryProps) {
  const { localization } = useConfig();
  const theme = useTheme();

  const isAdmin = isAdminPayout(payout);
  const amount = isAdmin ? payout.payout_summary.amount : payout.amount;
  const isInstant = isAdmin
    ? payout.payout_summary.is_instant
    : payout.is_instant;
  const payoutId = isAdmin ? payout.payout_summary.payout_id : payout.payout_id;

  const payoutAmount = new MoneyFactory(payout.currency)
    .create(amount)
    .format();

  const status =
    payout.status === undefined ? (
      <SummaryValue dataTestId="payout-status-text">N/A</SummaryValue>
    ) : (
      <StatusChip
        label={payoutStatusLabels[payout.status]}
        color={theme.palette[payoutStatusColors[payout.status]].main}
        dataTestId="payout-status-text"
      />
    );

  const accountDetails =
    [payout.bank_name, payout.last4 && `••••${payout.last4}`]
      .filter(Boolean)
      .join(" ") || "N/A";

  const hasArrived = payout.status === PayoutStatus.Completed;

  const arrivalDate =
    payout.status === PayoutStatus.Failed ||
    payout.status === PayoutStatus.Canceled
      ? undefined
      : payout.arrival_date;

  const formatDate = (date: Date) =>
    formatPayoutDate(date, localization.locale, localization.timeZone);

  return (
    <Stack
      id="summary"
      sx={{
        mb: { xs: 3, lg: 5 },
        py: { xs: 3, lg: 6 },
        backgroundColor: "primary._states.hover",
        borderRadius: 2,
        scrollMarginTop: theme.spacing(10),
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(4, 1fr)",
          },
          columnGap: 5,
          rowGap: 2.5,
          px: { xs: 2, sm: 3, lg: 6 },
        }}
      >
        <SummaryField label="Payout">
          <Typography
            variant="h5"
            sx={{ fontWeight: "bold" }}
            data-testid="payout-amount-text"
          >
            {payoutAmount}
          </Typography>
        </SummaryField>
        <SummaryField label="Destination">
          <SummaryValue dataTestId="payout-account-text">
            {accountDetails}
          </SummaryValue>
        </SummaryField>
        <SummaryField label="Statement descriptor">
          <SummaryValue dataTestId="payout-statement-descriptor-text">
            {payout.statement_descriptor || "N/A"}
          </SummaryValue>
        </SummaryField>
        <SummaryField label="Method">
          <SummaryValue dataTestId="payout-method-text">
            {isInstant ? "Instant" : "Standard"}
          </SummaryValue>
        </SummaryField>
        <SummaryField label="Status">{status}</SummaryField>
        <SummaryField label="Initiated">
          <SummaryValue dataTestId="payout-initiated-text">
            {formatDate(payout.created)}
          </SummaryValue>
        </SummaryField>
        <SummaryField label="Arrival Date">
          <SummaryValue dataTestId="payout-arrival-date-text">
            {arrivalDate === undefined
              ? "N/A"
              : `${hasArrived ? "" : "Est. "}${formatDate(arrivalDate)}`}
          </SummaryValue>
        </SummaryField>
        <SummaryField label="Payout ID">
          <Stack flexDirection="row" alignItems="center" gap={0.5}>
            <SummaryValue dataTestId="payout-id-text">
              {payoutId || "N/A"}
            </SummaryValue>
            <CopyButton
              value={payoutId ?? ""}
              label="Copy payout ID"
              dataTestId="payout-id-copy-button"
            />
          </Stack>
        </SummaryField>
      </Box>
    </Stack>
  );
}
